import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  HardDrive, Image, Video, TrendingDown, RefreshCw, Loader2,
  Database, BarChart3, ArrowDown, Shield, Globe, Zap,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatBytes(bytes, decimals = 1) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
}

function StatCard({ icon: Icon, label, value, sub, color, testId }) {
  return (
    <div className={`rounded-xl border p-5 bg-white hover:shadow-md transition-shadow`} data-testid={testId}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <span className="text-sm text-gray-500 font-medium">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function MiniBar({ label, value, max, color }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="w-20 text-gray-500 shrink-0">{label}</span>
      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-20 text-right font-mono text-gray-700">{formatBytes(value)}</span>
    </div>
  );
}

const MONTH_NAMES = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function MediaAnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/media/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch analytics');
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAnalytics(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-500">Loading media analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={fetchAnalytics}>Retry</Button>
      </div>
    );
  }

  const s = data?.summary || {};
  const byType = data?.by_type || {};
  const monthly = data?.monthly_trends || [];
  const hist = data?.historical || {};
  const cache = data?.cache || {};
  const cdn = data?.cdn || {};
  const hasData = s.total_tracked_uploads > 0;

  // Build monthly chart data (aggregate by month)
  const monthlyMap = {};
  monthly.forEach((m) => {
    const key = `${m.year}-${String(m.month).padStart(2, '0')}`;
    if (!monthlyMap[key]) {
      monthlyMap[key] = { label: `${MONTH_NAMES[m.month]} ${m.year}`, images: 0, videos: 0, original: 0, compressed: 0 };
    }
    if (m.media_type === 'image') monthlyMap[key].images += m.count;
    else monthlyMap[key].videos += m.count;
    monthlyMap[key].original += m.original_bytes;
    monthlyMap[key].compressed += m.compressed_bytes;
  });
  const monthlyArr = Object.values(monthlyMap);
  const maxMonthly = Math.max(...monthlyArr.map((m) => m.original), 1);

  const handleMigrateToCdn = async () => {
    setMigrating(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/media/migrate-to-cdn?batch_size=100`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Migration failed');
      const result = await res.json();
      setMigrationResult(result);
      fetchAnalytics();
    } catch (e) {
      setMigrationResult({ error: e.message });
    } finally {
      setMigrating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Media Analytics</h2>
          <p className="text-sm text-gray-500">Storage usage, compression performance, and growth trends</p>
        </div>
        <Button variant="outline" onClick={fetchAnalytics} disabled={loading} data-testid="refresh-analytics-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-summary">
        <StatCard
          icon={HardDrive}
          label="Total Uploads"
          value={s.total_tracked_uploads?.toLocaleString() || '0'}
          sub={`${(byType.image?.count || 0).toLocaleString()} images, ${(byType.video?.count || 0).toLocaleString()} videos`}
          color="bg-blue-100 text-blue-600"
          testId="stat-total-uploads"
        />
        <StatCard
          icon={TrendingDown}
          label="Storage Saved"
          value={formatBytes(s.total_savings_bytes)}
          sub={`${s.savings_percentage || 0}% reduction`}
          color="bg-emerald-100 text-emerald-600"
          testId="stat-storage-saved"
        />
        <StatCard
          icon={Database}
          label="Actual Storage Used"
          value={formatBytes(s.total_compressed_bytes)}
          sub={`vs ${formatBytes(s.total_original_bytes)} without compression`}
          color="bg-purple-100 text-purple-600"
          testId="stat-actual-storage"
        />
        <StatCard
          icon={Shield}
          label="Signed URL Cache"
          value={cache.active_cached_urls?.toLocaleString() || '0'}
          sub={`${cache.total_cached_urls || 0} total (fallback)`}
          color="bg-amber-100 text-amber-600"
          testId="stat-url-cache"
        />
      </div>

      {/* CDN Coverage Quick Stat */}
      <div className="bg-gradient-to-r from-cyan-50 to-emerald-50 border border-cyan-200 rounded-xl p-4 flex items-center justify-between" data-testid="cdn-banner">
        <div className="flex items-center gap-3">
          <Globe className="h-6 w-6 text-cyan-600" />
          <div>
            <p className="font-semibold text-gray-900">CDN Delivery Active</p>
            <p className="text-xs text-gray-500">{cdn.total_cdn_urls || 0} files on Google CDN — {cdn.cdn_coverage_pct || 0}% coverage</p>
          </div>
        </div>
        {cdn.migration_remaining > 0 && (
          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">
            {cdn.migration_remaining} files pending
          </span>
        )}
      </div>

      {/* Compression by Type */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Images */}
        <Card className="border">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Image className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold text-gray-900">Image Compression</h3>
            </div>
            {byType.image ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xl font-bold text-gray-900">{byType.image.count.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">Total</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xl font-bold text-blue-700">{formatBytes(byType.image.total_compressed_bytes)}</p>
                    <p className="text-xs text-blue-600">Stored</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3">
                    <p className="text-xl font-bold text-emerald-700">{formatBytes(byType.image.savings_bytes)}</p>
                    <p className="text-xs text-emerald-600">Saved</p>
                  </div>
                </div>
                <MiniBar label="Original" value={byType.image.total_original_bytes} max={byType.image.total_original_bytes} color="bg-gray-300" />
                <MiniBar label="Compressed" value={byType.image.total_compressed_bytes} max={byType.image.total_original_bytes} color="bg-blue-500" />
                <p className="text-xs text-gray-400 text-center">
                  Avg ratio: {((1 - (byType.image.avg_compression_ratio || 1)) * 100).toFixed(0)}% smaller
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-400 py-6 text-center">No image upload data yet</p>
            )}
          </CardContent>
        </Card>

        {/* Videos */}
        <Card className="border">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Video className="h-5 w-5 text-purple-600" />
              <h3 className="font-semibold text-gray-900">Video Compression</h3>
            </div>
            {byType.video ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xl font-bold text-gray-900">{byType.video.count.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">Total</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-xl font-bold text-purple-700">{formatBytes(byType.video.total_compressed_bytes)}</p>
                    <p className="text-xs text-purple-600">Stored</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3">
                    <p className="text-xl font-bold text-emerald-700">{formatBytes(byType.video.savings_bytes)}</p>
                    <p className="text-xs text-emerald-600">Saved</p>
                  </div>
                </div>
                <MiniBar label="Original" value={byType.video.total_original_bytes} max={byType.video.total_original_bytes} color="bg-gray-300" />
                <MiniBar label="Compressed" value={byType.video.total_compressed_bytes} max={byType.video.total_original_bytes} color="bg-purple-500" />
                <p className="text-xs text-gray-400 text-center">
                  Avg ratio: {((1 - (byType.video.avg_compression_ratio || 1)) * 100).toFixed(0)}% smaller
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-400 py-6 text-center">No video upload data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Monthly Trend */}
      <Card className="border">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="h-5 w-5 text-indigo-600" />
            <h3 className="font-semibold text-gray-900">Monthly Growth</h3>
          </div>
          {monthlyArr.length > 0 ? (
            <div className="space-y-3">
              {monthlyArr.map((m, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="w-24 text-sm text-gray-600 shrink-0">{m.label}</span>
                  <div className="flex-1 flex gap-1 h-7">
                    <div
                      className="bg-blue-400 rounded-l transition-all duration-300 flex items-center justify-center"
                      style={{ width: `${(m.original / maxMonthly) * 100}%`, minWidth: m.original > 0 ? '2px' : 0 }}
                      title={`Original: ${formatBytes(m.original)}`}
                    />
                    <div
                      className="bg-emerald-500 rounded-r transition-all duration-300 flex items-center justify-center"
                      style={{ width: `${(m.compressed / maxMonthly) * 100}%`, minWidth: m.compressed > 0 ? '2px' : 0 }}
                      title={`Compressed: ${formatBytes(m.compressed)}`}
                    />
                  </div>
                  <div className="w-32 text-right shrink-0">
                    <span className="text-xs text-gray-500">{m.images + m.videos} files</span>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-4 pt-3 border-t text-xs text-gray-400">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-400" /> Original</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500" /> Compressed</span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400 py-6 text-center">Monthly data will appear as uploads are tracked</p>
          )}
        </CardContent>
      </Card>

      {/* Historical + Estimation */}
      <Card className="border">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Database className="h-5 w-5 text-slate-600" />
            <h3 className="font-semibold text-gray-900">Historical Data (Estimated)</h3>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-2xl font-bold text-gray-900">{hist.total_gs_urls_in_answers?.toLocaleString() || 0}</p>
              <p className="text-xs text-gray-500">Total media in DB</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-2xl font-bold text-gray-900">{hist.inspections_with_media?.toLocaleString() || 0}</p>
              <p className="text-xs text-gray-500">Inspections with media</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-2xl font-bold text-gray-900">{hist.estimated_storage_gb || 0} GB</p>
              <p className="text-xs text-gray-500">Est. Firebase storage</p>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-3 text-center">
            Estimates based on average compressed image size of ~300KB per file
          </p>
        </CardContent>
      </Card>

      {/* CDN Delivery */}
      <Card className="border">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-cyan-600" />
              <h3 className="font-semibold text-gray-900">CDN Delivery</h3>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleMigrateToCdn}
              disabled={migrating || (cdn.migration_remaining === 0)}
              data-testid="migrate-cdn-btn"
            >
              {migrating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
              {migrating ? 'Migrating...' : cdn.migration_remaining > 0 ? `Migrate ${cdn.migration_remaining} files` : 'All Migrated'}
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center mb-4">
            <div className="bg-cyan-50 rounded-lg p-4">
              <p className="text-2xl font-bold text-cyan-700">{cdn.total_cdn_urls?.toLocaleString() || 0}</p>
              <p className="text-xs text-cyan-600">CDN URLs Cached</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-2xl font-bold text-gray-700">{cdn.migration_remaining?.toLocaleString() || 0}</p>
              <p className="text-xs text-gray-500">Pending Migration</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-4">
              <p className="text-2xl font-bold text-emerald-700">{cdn.cdn_coverage_pct || 0}%</p>
              <p className="text-xs text-emerald-600">CDN Coverage</p>
            </div>
          </div>
          {/* CDN Coverage Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-gray-500">
              <span>CDN coverage</span>
              <span>{cdn.cdn_coverage_pct || 0}%</span>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-full transition-all duration-700"
                style={{ width: `${cdn.cdn_coverage_pct || 0}%` }}
              />
            </div>
          </div>
          {migrationResult && !migrationResult.error && (
            <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
              <p className="text-green-800">
                Migrated <strong>{migrationResult.migrated}</strong> files to CDN
                {migrationResult.remaining_estimate > 0 && (
                  <> ({migrationResult.remaining_estimate} remaining — click again)</>
                )}
              </p>
            </div>
          )}
          {migrationResult?.error && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {migrationResult.error}
            </div>
          )}
          <p className="text-xs text-gray-400 mt-3">
            CDN URLs are permanent, cacheable, and served via Google's global CDN. No expiry, no regeneration needed.
          </p>
        </CardContent>
      </Card>

      {/* Empty state */}
      {!hasData && (
        <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed">
          <ArrowDown className="h-8 w-8 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No tracked upload data yet</p>
          <p className="text-sm text-gray-400 mt-1">
            Upload stats will populate as mechanics capture photos and videos during inspections.
            Historical estimates are shown from existing database records.
          </p>
        </div>
      )}
    </div>
  );
}
